import React from "react"

export function Icon({ name, size, className, style }) {
    const s = size ? { width: size, height: size } : null
    return (
        <svg className={"ic" + (className ? " " + className : "")} style={{ ...(s || {}), ...(style || {}) }} aria-hidden="true">
            <use href={"#ic-" + name} />
        </svg>
    )
}

export function Check({ size }) {
    return (
        <svg viewBox="0 0 16 16" width={size || 13} height={size || 13} aria-hidden="true">
            <path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    )
}

export function Sparkle({ size, className, style }) {
    const s = size ? { width: size, height: size } : null
    return (
        <svg className={"ic" + (className ? " " + className : "")} viewBox="0 0 24 24" fill="none" aria-hidden="true"
            style={{ ...(s || {}), ...(style || {}) }}>
            <path d="M11 1.6c.62 5.2 2.6 7.18 7.8 7.8-5.2.62-7.18 2.6-7.8 7.8-.62-5.2-2.6-7.18-7.8-7.8C8.4 8.78 10.38 6.8 11 1.6Z" fill="currentColor" />
            <path d="M18.4 13.2c.3 2.55 1.25 3.5 3.8 3.8-2.55.3-3.5 1.25-3.8 3.8-.3-2.55-1.25-3.5-3.8-3.8 2.55-.3 3.5-1.25 3.8-3.8Z" fill="currentColor" opacity="0.6" />
        </svg>
    )
}

function prefersReduced() {
    return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

export function Reveal({ children, delay = 0, as = "div", className = "", style = {} }) {
    const ref = React.useRef(null)
    const Tag = as

    React.useEffect(() => {
        const el = ref.current
        if (!el) return
        const vh = window.innerHeight || document.documentElement.clientHeight || 800
        const top = el.getBoundingClientRect().top
        if (prefersReduced() || top < vh * 0.92) return
        el.classList.add("armed")
        el.style.transitionDelay = delay + "ms"
        let done = false
        const show = () => { if (!done) { done = true; el.classList.add("in") } }
        let io
        if (typeof IntersectionObserver !== "undefined") {
            io = new IntersectionObserver(
                (es) => es.forEach((e) => { if (e.isIntersecting) { show(); io.disconnect() } }),
                { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
            )
            io.observe(el)
        } else { show() }
        const t = setTimeout(() => { el.style.transition = "none"; el.classList.remove("armed") }, 1600)
        return () => { io && io.disconnect(); clearTimeout(t) }
    }, [delay])

    return <Tag ref={ref} className={("reveal " + className).trim()} style={style}>{children}</Tag>
}

function useCountUp(target, decimals = 0, duration = 1400) {
    const [val, setVal] = React.useState(0)
    const ref = React.useRef(null)
    const started = React.useRef(false)

    React.useEffect(() => {
        const el = ref.current
        if (!el) return
        const run = () => {
            if (started.current) return
            started.current = true
            if (prefersReduced()) { setVal(target); return }
            const t0 = performance.now()
            const tick = (t) => {
                const p = Math.min(1, (t - t0) / duration)
                const eased = 1 - Math.pow(1 - p, 3)
                setVal(target * eased)
                if (p < 1) requestAnimationFrame(tick)
                else setVal(target)
            }
            requestAnimationFrame(tick)
        }
        const vh = window.innerHeight || 800
        const top = el.getBoundingClientRect().top
        if (top < vh * 0.95) { run(); return }
        let io
        if (typeof IntersectionObserver !== "undefined") {
            io = new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) run() }), { threshold: 0.4 })
            io.observe(el)
        }
        const t = setTimeout(() => { if (!started.current) setVal(target) }, 1800)
        return () => { io && io.disconnect(); clearTimeout(t) }
    }, [target, duration])

    return [val.toFixed(decimals), ref]
}

export function Stat({ value, suffix, label, decimals }) {
    const [shown, ref] = useCountUp(value, decimals)
    return (
        <div className="stat" ref={ref}>
            <div className="stat__v">{shown}<span className="u">{suffix}</span></div>
            <div className="stat__l">{label}</div>
        </div>
    )
}
